// src/pages/actividades/components/HistorialTimeline.tsx
import { Card } from '@/components/ui';
import { History, CheckCircle, RotateCcw, UserCheck, ArrowUpDown, AlertCircle } from 'lucide-react';
import { formatDateTime } from '@/lib/utils';
import { useActividadHistorial } from '@/hooks/queries/useActividades';
import type { ActividadHistorialItem } from '@/types';

const ACCION_CONFIG: Record<string, { icon: typeof History; color: string; label: string }> = {
  completada: { icon: CheckCircle, color: 'text-green-500', label: 'Completada' },
  reabierta: { icon: RotateCcw, color: 'text-orange-500', label: 'Reabierta' },
  estado_cambiado: { icon: ArrowUpDown, color: 'text-blue-500', label: 'Estado cambiado' },
  reasignada: { icon: UserCheck, color: 'text-purple-500', label: 'Reasignada' },
  prioridad_cambiada: { icon: AlertCircle, color: 'text-yellow-500', label: 'Prioridad cambiada' },
};

function getAccionConfig(accion: string) {
  return ACCION_CONFIG[accion] ?? { icon: History, color: 'text-gray-500', label: accion };
}

function formatValor(value: string | null | undefined): string {
  if (value == null) return '-';
  return value;
}

function HistorialEntry({ entry }: { entry: ActividadHistorialItem }) {
  const config = getAccionConfig(entry.accion);
  const Icon = config.icon;
  const usuario = entry.usuario
    ? `${entry.usuario.nombre} ${entry.usuario.apellido ?? ''}`.trim()
    : 'Sistema';

  const cambios = entry.valor_nuevo
    ? Object.entries(entry.valor_nuevo).map(([key, val]) => {
        const prev = entry.valor_anterior?.[key];
        return { key, from: formatValor(prev), to: formatValor(val) };
      })
    : [];

  return (
    <div className="flex gap-3 py-2">
      <div className={`mt-0.5 ${config.color}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 text-sm">
          <span className="font-medium">{config.label}</span>
          <span className="text-gray-500">por {usuario}</span>
        </div>
        {cambios.length > 0 && (
          <div className="text-xs text-gray-500 mt-0.5">
            {cambios.map((c) => (
              <span key={c.key}>
                {c.from} → {c.to}
              </span>
            ))}
          </div>
        )}
        <div className="text-xs text-gray-400 mt-0.5">
          {formatDateTime(entry.created_at)}
        </div>
      </div>
    </div>
  );
}

export function HistorialTimeline({ actividadId }: { actividadId: string }) {
  const { data: historial, isLoading, isError } = useActividadHistorial(actividadId);

  if (isLoading) {
    return (
      <Card className="p-4">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <History className="h-4 w-4" />
          Historial
        </h3>
        <p className="text-sm text-gray-400">Cargando...</p>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="p-4">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <History className="h-4 w-4" />
          Historial
        </h3>
        <p className="text-sm text-red-500">No se pudo cargar el historial</p>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <h3 className="font-semibold mb-3 flex items-center gap-2">
        <History className="h-4 w-4" />
        Historial
      </h3>
      {!historial || historial.length === 0 ? (
        <p className="text-sm text-gray-400">Sin cambios registrados</p>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {historial.map((entry) => (
            <HistorialEntry key={entry.id} entry={entry} />
          ))}
        </div>
      )}
    </Card>
  );
}
